/**
 * BoardSettingsModal - modal for editing board settings (name, description, view settings)
 */

import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { X, Trash2, AlertTriangle, Archive, ArchiveRestore } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBoardStore } from '@stores/boardStore';
import { toast } from '@components/ui/Toast';
import type { BoardDetail } from '@services/api';

interface BoardSettingsModalProps {
  board: BoardDetail | null;
  open: boolean;
  onClose: () => void;
  onDelete?: () => void;
}

export default function BoardSettingsModal({
  board,
  open,
  onClose,
  onDelete,
}: BoardSettingsModalProps) {
  const { t } = useTranslation();
  const { updateBoard, deleteBoard, archiveBoard, restoreBoard } = useBoardStore();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showLabels, setShowLabels] = useState(true);
  const [showAssignee, setShowAssignee] = useState(true);
  const [showDueDate, setShowDueDate] = useState(true);
  const [showPriority, setShowPriority] = useState(true);
  const [showSubtaskProgress, setShowSubtaskProgress] = useState(true);
  const [showAttachments, setShowAttachments] = useState(true);
  const [compactMode, setCompactMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset form when board changes
  useEffect(() => {
    if (board) {
      setName(board.name);
      setDescription(board.description || '');
      setShowLabels(board.viewSettings?.showLabels ?? true);
      setShowAssignee(board.viewSettings?.showAssignee ?? true);
      setShowDueDate(board.viewSettings?.showDueDate ?? true);
      setShowPriority(board.viewSettings?.showPriority ?? true);
      setShowSubtaskProgress(board.viewSettings?.showSubtaskProgress ?? true);
      setShowAttachments(board.viewSettings?.showAttachments ?? true);
      setCompactMode(board.viewSettings?.compactMode ?? false);
      setShowDeleteConfirm(false);
    }
  }, [board]);

  const handleSave = async () => {
    if (!board || !name.trim()) return;

    setIsSaving(true);
    try {
      let hasChanges = false;

      if (name.trim() !== board.name) {
        hasChanges = true;
      }

      if (description.trim() !== (board.description || '')) {
        hasChanges = true;
      }

      // Check if view settings changed
      const currentViewSettings = board.viewSettings;
      const viewSettingsChanged =
        showLabels !== currentViewSettings?.showLabels ||
        showAssignee !== currentViewSettings?.showAssignee ||
        showDueDate !== currentViewSettings?.showDueDate ||
        showPriority !== currentViewSettings?.showPriority ||
        showSubtaskProgress !== currentViewSettings?.showSubtaskProgress ||
        showAttachments !== currentViewSettings?.showAttachments ||
        compactMode !== currentViewSettings?.compactMode;

      if (viewSettingsChanged) {
        hasChanges = true;
      }

      if (hasChanges) {
        const updateData: { name: string; description?: string; viewSettings: typeof board.viewSettings } = {
          name: name.trim(),
          viewSettings: {
            showLabels,
            showAssignee,
            showDueDate,
            showPriority,
            showSubtaskProgress,
            showAttachments,
            compactMode,
          },
        };
        if (description.trim()) {
          updateData.description = description.trim();
        }
        await updateBoard(board.id, updateData);
        toast.success(t('board.boardUpdated'));
      }

      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('board.failedUpdateBoard');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const handleArchive = async () => {
    if (!board) return;
    setIsSaving(true);
    try {
      await archiveBoard(board.id);
      toast.success(t('board.boardArchived'));
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('board.failedArchiveBoard');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!board) return;
    setIsSaving(true);
    try {
      await restoreBoard(board.id);
      toast.success(t('board.boardRestored'));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('board.failedRestoreBoard');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!board) return;

    setIsSaving(true);
    try {
      await deleteBoard(board.id);
      toast.success(t('board.boardDeleted'));
      onClose();
      onDelete?.();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('board.failedDeleteBoard');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!board) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-50 bg-black/50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'max-h-[90vh] overflow-y-auto',
            'rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('board.settingsTitle')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded p-1 transition-colors',
                  'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                  'dark:hover:bg-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('board.basicInfo')}
              </h3>

              {/* Name */}
              <div>
                <label
                  htmlFor="board-name"
                  className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('common.name', 'Name')}
                </label>
                <input
                  id="board-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={clsx(
                    'w-full rounded-md border px-3 py-2',
                    'border-neutral-300 bg-white text-neutral-900',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                    'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                  )}
                  placeholder={t('board.namePlaceholder')}
                />
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="board-description"
                  className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('task.description')}
                </label>
                <textarea
                  id="board-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className={clsx(
                    'w-full rounded-md border px-3 py-2',
                    'border-neutral-300 bg-white text-neutral-900',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                    'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                  )}
                  placeholder={t('board.descriptionPlaceholder')}
                />
              </div>
            </div>

            {/* View Settings Section */}
            <div className="space-y-4 border-t border-neutral-200 pt-4 dark:border-neutral-700">
              <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('board.cardDisplayOptions')}
              </h3>

              <div className="space-y-3">
                <ToggleOption
                  label={t('board.showLabels')}
                  description={t('board.showLabelsDesc')}
                  checked={showLabels}
                  onChange={setShowLabels}
                />
                <ToggleOption
                  label={t('board.showAssignee')}
                  description={t('board.showAssigneeDesc')}
                  checked={showAssignee}
                  onChange={setShowAssignee}
                />
                <ToggleOption
                  label={t('board.showDueDate')}
                  description={t('board.showDueDateDesc')}
                  checked={showDueDate}
                  onChange={setShowDueDate}
                />
                <ToggleOption
                  label={t('board.showPriority')}
                  description={t('board.showPriorityDesc')}
                  checked={showPriority}
                  onChange={setShowPriority}
                />
                <ToggleOption
                  label={t('board.showSubtaskProgress')}
                  description={t('board.showSubtaskProgressDesc')}
                  checked={showSubtaskProgress}
                  onChange={setShowSubtaskProgress}
                />
                <ToggleOption
                  label={t('board.showAttachments')}
                  description={t('board.showAttachmentsDesc')}
                  checked={showAttachments}
                  onChange={setShowAttachments}
                />
                <ToggleOption
                  label={t('board.compactMode')}
                  description={t('board.compactModeDesc')}
                  checked={compactMode}
                  onChange={setCompactMode}
                />
              </div>
            </div>
          </div>

          {/* Archive section — mirrors project archiving: hidden, not deleted */}
          <div className="mt-6 border-t border-neutral-200 pt-4 dark:border-neutral-700">
            {board?.isArchived ? (
              <button
                type="button"
                onClick={handleRestore}
                disabled={isSaving}
                className={clsx(
                  'flex items-center gap-2 text-sm',
                  'text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                <ArchiveRestore className="h-4 w-4" />
                {t('board.restoreBoard')}
              </button>
            ) : !showArchiveConfirm ? (
              <button
                type="button"
                onClick={() => setShowArchiveConfirm(true)}
                className={clsx(
                  'flex items-center gap-2 text-sm',
                  'text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300'
                )}
              >
                <Archive className="h-4 w-4" />
                {t('board.archiveBoard')}
              </button>
            ) : (
              <div className="rounded-md bg-amber-50 p-3 dark:bg-amber-900/20">
                <div className="flex items-start gap-2">
                  <Archive className="h-5 w-5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                      {t('board.archiveBoardConfirm')}
                    </p>
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                      {t('board.archiveBoardWarning')}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={handleArchive}
                        disabled={isSaving}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-amber-600 text-white hover:bg-amber-700',
                          'disabled:cursor-not-allowed disabled:opacity-50'
                        )}
                      >
                        {t('board.archiveBoard')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowArchiveConfirm(false)}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-neutral-200 text-neutral-700 hover:bg-neutral-300',
                          'dark:bg-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-500'
                        )}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Delete section */}
          <div className="mt-4 border-t border-neutral-200 pt-4 dark:border-neutral-700">
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className={clsx(
                  'flex items-center gap-2 text-sm',
                  'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                )}
              >
                <Trash2 className="h-4 w-4" />
                {t('board.deleteBoard')}
              </button>
            ) : (
              <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {t('board.deleteBoardConfirm')}
                    </p>
                    <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                      {t('board.deleteBoardWarning')}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isSaving}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-red-600 text-white hover:bg-red-700',
                          'disabled:cursor-not-allowed disabled:opacity-50'
                        )}
                      >
                        {t('common.delete')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className={clsx(
                          'rounded px-3 py-1 text-sm font-medium',
                          'bg-neutral-200 text-neutral-700 hover:bg-neutral-300',
                          'dark:bg-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-500'
                        )}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                'dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-primary-600 text-white hover:bg-primary-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isSaving ? t('common.saving') : t('common.saveChanges')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ToggleOptionProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleOption({ label, description, checked, onChange }: ToggleOptionProps) {
  return (
    <label className="flex items-center justify-between">
      <div>
        <span className="text-sm text-neutral-900 dark:text-white">{label}</span>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full',
          'border-2 border-transparent transition-colors duration-200 ease-in-out',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          checked ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-600'
        )}
      >
        <span
          className={clsx(
            'pointer-events-none inline-block h-5 w-5 transform rounded-full',
            'bg-white shadow ring-0 transition duration-200 ease-in-out',
            checked ? 'translate-x-5' : 'translate-x-0'
          )}
        />
      </button>
    </label>
  );
}
